import { ContactsGeneralSearch } from "@/components/features/contact/ContactsGeneralSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  AddContactSlideover,
  EMBEDDED_CREATE_CONTACT_FORM_ID,
  type createContactFormSchema,
} from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { EditContactForm } from "@/components/features/contact/AddOrEditContactModal/EditContactForm";
import { EDIT_CONTACT_MODAL_FORM_ID } from "@/components/features/contact/AddOrEditContactModal/EditContactModal";
import ContactsSearchBar from "@/components/features/contact/ContactsSearchBar/ContactsSearchBar";
import { ContactsTable } from "@/components/features/contact/ContactsTable/ContactsTable";
import { GradientCardProfileDetailRow } from "@/components/features/matter/information/GradientCardProfileDetailRow";
import {
  MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS,
  MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS,
  MatterClientHeroPreviewCard,
} from "@/components/features/matter/information/MatterClientHeroPreviewCard";
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LabelText,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { Switch } from "@/components/ui/shadcn/Switch/Switch";
import { useUser } from "@/components/UserProvider";
import { useSearchObject } from "@/hooks/useSearchObject";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Contact, Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import {
  CONTACT_ROLE_SELECT_CARD_COMPACT_HEIGHT_CLASS,
  CONTACT_ROLE_SELECT_CARD_HEIGHT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_ACTION_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BODY_WITH_ACTIONS_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_COMPACT_CLASS,
} from "@/lib/utils/contact-role-select-card-class";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  formatAddress,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { getContactHeroTypeIcon } from "@/lib/utils/iconUtils";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MapPin,
  Pencil,
  Phone,
  PlusCircle,
  UserCheck,
  X,
} from "lucide-react";
import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { type z } from "zod";
import {
  ContactPreviewChip,
  type ContactPreviewChipContact,
} from "../../ContactPreviewChip/ContactPreviewChip";

function compactRelatedContactDisplayName(contact: Contact): string {
  const withOptionalName = contact as Contact & { contactName?: string };
  const fromFormat = formatContactsName(contact);
  const fromOrg = contact.organizationName?.trim();
  const fromGroup = contact.groupName?.trim();
  const fromContactName = withOptionalName.contactName?.trim();
  return String(fromFormat || fromOrg || fromGroup || fromContactName || "—");
}

function compactRelatedContactDetailLine(contact: Contact): string {
  let phoneValue = "";
  const phones = Array.isArray(contact.phones) ? contact.phones : [];
  if (phones.length > 0) {
    const phone = phones[0];
    if (phone.number) {
      phoneValue = formatLocalOrIntlPhoneNumber(phone) || phone.number;
    }
  }
  let emailValue = "";
  const emails = Array.isArray(contact.emails) ? contact.emails : [];
  if (emails.length > 0) {
    const addr = emails[0].address;
    if (addr) {
      emailValue = addr;
    }
  }
  if (phoneValue && emailValue) {
    return `${phoneValue} | ${emailValue}`;
  }
  if (phoneValue) {
    return phoneValue;
  }
  if (emailValue) {
    return emailValue;
  }
  return "—";
}

function getContactRecordId(
  contact?: (Partial<Contact> & { contactId?: string }) | null,
): string {
  return contact?.id ?? contact?.contactId ?? "";
}

function readMultiSelectContactsField(raw: unknown): Contact[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((c): c is Contact =>
    Boolean(getContactRecordId(c as Contact | undefined)),
  );
}

/**
 * Multi-select contact pickers read `getValues` here (not `contacts` from render) so
 * rapid double-clicks / overlapping async row handlers cannot append the same ID twice.
 */
function appendContactToMultiSelectField(
  form: {
    getValues: (name: string) => unknown;
    setValue: (
      name: string,
      value: unknown,
      options?: { shouldDirty?: boolean },
    ) => void;
  },
  fieldName: string,
  contact: Contact,
): Contact[] {
  const existing = readMultiSelectContactsField(form.getValues(fieldName));
  if (!contact.id) {
    return existing;
  }
  if (existing.some((c) => c.id === contact.id)) {
    return existing;
  }
  const next = [...existing, contact];
  form.setValue(fieldName, next, { shouldDirty: true });
  return next;
}

type CompactContactRoleTone = "default" | "client" | "defendant";

function compactContactRoleLabel(tone: CompactContactRoleTone): string | null {
  if (tone === "client") return "Client";
  if (tone === "defendant") return "Defendant";
  return null;
}

function compactContactGradientClass(tone: CompactContactRoleTone): string {
  if (tone === "client") return MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS;
  if (tone === "defendant") return MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS;
  return MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS;
}

function matterHeroToneFromCompactRole(
  tone: CompactContactRoleTone,
): "green" | "gray" | "blue" {
  if (tone === "client") return "green";
  if (tone === "defendant") return "blue";
  return "gray";
}

/** Primary CTA copy for role-based contact slots (e.g. "Select driver"). */
export function contactRoleSelectPrompt(label?: string): string {
  const trimmed = label?.trim();
  if (!trimmed) return "Select a contact";
  return `Select ${trimmed.replace(/\b[A-Z][a-z]+\b/g, (word) => word.toLowerCase())}`;
}

function contactRoleSlotLabel(label?: string): string {
  const trimmed = label?.replace(/\*$/, "").trim();
  return trimmed || "Contact";
}

const COMPACT_RELATED_CONTACT_DETAILS_KEY_PREFIX =
  "compact-related-contact-details-hidden";

function pickPrimaryPhoneForCompactCard(contact: Contact) {
  const phones = Array.isArray(contact.phones) ? contact.phones : [];
  return phones.find((p) => p.isPrimary) ?? phones.at(0);
}

function pickPrimaryEmailForCompactCard(contact: Contact) {
  const emails = Array.isArray(contact.emails) ? contact.emails : [];
  return emails.find((e) => e.isPrimary) ?? emails.at(0);
}

function pickPrimaryAddressForCompactCard(contact: Contact) {
  const addresses = Array.isArray(contact.addresses) ? contact.addresses : [];
  return addresses.find((a) => a.isPrimary) ?? addresses.at(0);
}

function CompactRelatedContactHeroCard({
  label,
  contact,
  matter,
  compactRoleTone,
  compactRoleLabel,
  collapsedHeightClass,
  onEditContact,
  editLoading,
  onDelete,
  disableDelete,
  onChangeContact,
  changeContactLabel = "Change",
}: {
  label?: string;
  contact: Contact;
  matter?: Matter;
  matterId?: string;
  compactRoleTone: CompactContactRoleTone;
  compactRoleLabel: string | null;
  collapsedHeightClass: string;
  onEditContact: () => void;
  editLoading: boolean;
  onDelete?: (c: Contact) => void;
  disableDelete?: boolean;
  /** Opens the contact picker to choose a different contact (e.g. compact selector row). */
  onChangeContact?: () => void;
  changeContactLabel?: string;
}) {
  const { hasActionPermission } = useUser();
  const canEditContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );

  const slotLabel = contactRoleSlotLabel(label);
  const detailsHiddenStorageKey = useMemo(
    () =>
      [
        COMPACT_RELATED_CONTACT_DETAILS_KEY_PREFIX,
        matter?.id,
        slotLabel.trim().toLowerCase().replace(/\s+/g, "-"),
        contact.id || "no-contact",
      ].join(":"),
    [contact.id, matter?.id, slotLabel],
  );

  const [detailsHidden, setDetailsHidden] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      return true;
    }
    return storedValue === "true";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      setDetailsHidden(true);
      return;
    }
    setDetailsHidden(storedValue === "true");
  }, [detailsHiddenStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(detailsHiddenStorageKey, String(detailsHidden));
  }, [detailsHidden, detailsHiddenStorageKey]);

  const primaryPhone = pickPrimaryPhoneForCompactCard(contact);
  const primaryEmail = pickPrimaryEmailForCompactCard(contact);
  const primaryAddress = pickPrimaryAddressForCompactCard(contact);
  const primaryPhoneNumber = primaryPhone?.number;
  const formattedPhone = primaryPhone
    ? formatLocalOrIntlPhoneNumber(primaryPhone)
    : "";
  const formattedAddress = primaryAddress
    ? formatAddress({
        street1: primaryAddress.street1 ?? undefined,
        street2: primaryAddress.street2 ?? undefined,
        city: primaryAddress.city ?? undefined,
        state: primaryAddress.state ?? "",
        zipCode: primaryAddress.zipCode ?? undefined,
        country:
          primaryAddress.country === "United States"
            ? undefined
            : (primaryAddress.country ?? undefined),
      })
    : "";

  const CompactHeroTypeIcon = getContactHeroTypeIcon(contact.type);

  return (
    <div
      className={cn(
        "relative flex w-full min-w-0 flex-col self-start overflow-hidden",
        compactContactGradientClass(compactRoleTone),
        "p-0",
      )}
    >
      <div className="pointer-events-none absolute right-[-12px] bottom-[-30px] z-0 text-[6.5rem] leading-none font-semibold text-white/6 select-none">
        {compactRelatedContactDisplayName(contact)
          .trim()
          .charAt(0)
          .toUpperCase() || "C"}
      </div>

      <div
        className={cn(
          "relative z-[2] box-border flex w-full flex-col overflow-hidden px-3.5 py-3",
          collapsedHeightClass,
        )}
      >
        <div className="relative z-[2] flex w-full flex-col">
          <div className="flex min-w-0 shrink-0 items-center justify-between gap-1.5">
            <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-1.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-white/12">
                <CompactHeroTypeIcon className="h-3.5 w-3.5 text-white" />
              </span>
              <p className="min-w-0 truncate text-sm leading-tight font-semibold text-white/85">
                {slotLabel}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-0">
              {canEditContact ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="relative z-[3] h-7 w-7 shrink-0 rounded-full text-white/80 hover:bg-white/12 hover:text-white"
                  disabled={editLoading}
                  onClick={onEditContact}
                >
                  {editLoading ? (
                    <LoadingSpinner className="h-3.5 w-3.5" />
                  ) : (
                    <Pencil className="h-3.5 w-3.5" />
                  )}
                </Button>
              ) : null}
              {onDelete && !disableDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="relative z-[3] h-7 w-7 shrink-0 rounded-full text-white/80 hover:bg-white/12 hover:text-white"
                  onClick={() => {
                    onDelete(contact);
                  }}
                  data-testid="contact-preview-chip-delete"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="pointer-events-none mt-1 flex min-w-0 shrink-0 flex-col justify-start overflow-hidden text-left">
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="min-w-0 truncate text-base leading-snug font-semibold tracking-tight text-white">
                {compactRelatedContactDisplayName(contact)}
              </p>
              {compactRoleLabel ? (
                <span className="inline-flex shrink-0 rounded-full border border-white/20 bg-white/5 px-1.5 py-0.5 text-[10px] leading-none font-semibold text-white/75">
                  {compactRoleLabel}
                </span>
              ) : null}
            </div>
            <div className="pointer-events-auto mt-0.5 flex min-w-0 items-center gap-1">
              <p className="line-clamp-2 min-w-0 flex-1 text-sm leading-snug text-white/85">
                {compactRelatedContactDetailLine(contact)}
              </p>
              <button
                type="button"
                className="-mr-1 shrink-0 rounded p-0.5 text-white/80 hover:bg-white/12 hover:text-white"
                aria-expanded={!detailsHidden}
                aria-label={
                  detailsHidden
                    ? "Show phone, email, and address"
                    : "Hide phone, email, and address"
                }
                onClick={() => {
                  setDetailsHidden((current) => !current);
                }}
              >
                {detailsHidden ? (
                  <ChevronRight className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>

          {onChangeContact ? (
            <div className="relative z-[3] mt-2 flex shrink-0 items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-white/80 hover:bg-white/12 hover:text-white"
                onClick={onChangeContact}
              >
                {changeContactLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          "relative z-10 overflow-hidden transition-[max-height,opacity,margin-top] duration-200 ease-out",
          detailsHidden
            ? "mt-0 max-h-0 opacity-0"
            : "mt-2 max-h-80 px-3.5 pb-3 opacity-100",
        )}
        aria-hidden={detailsHidden}
      >
        <div className="relative z-10 space-y-2 rounded-2xl border border-white/20 bg-white/12 p-2.5 shadow-sm backdrop-blur-md">
          <GradientCardProfileDetailRow
            icon={Phone}
            label="Phone"
            value={formattedPhone}
            onCopy={
              primaryPhoneNumber
                ? () => void copyToClipboard(primaryPhoneNumber, "Phone number")
                : undefined
            }
          />
          <GradientCardProfileDetailRow
            icon={Mail}
            label="Email"
            value={primaryEmail?.address ? primaryEmail.address : undefined}
            onCopy={
              primaryEmail?.address
                ? () =>
                    void copyToClipboard(
                      primaryEmail.address ?? "",
                      "Email address",
                    )
                : undefined
            }
          />
          <GradientCardProfileDetailRow
            icon={MapPin}
            label="Address"
            value={formattedAddress}
            onCopy={
              formattedAddress
                ? () => void copyToClipboard(formattedAddress, "Address")
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}

export function ContactPickerShell({
  open,
  setOpen,
  children,
  modalCreate,
  modalEdit,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: ReactNode;
  modalCreate?: {
    open: boolean;
    setOpen: (open: boolean) => void;
    isDirty: boolean;
    canSubmit: boolean;
    isLoading: boolean;
    content: ReactNode;
  };
  modalEdit?: {
    open: boolean;
    setOpen: (open: boolean) => void;
    isDirty: boolean;
    canSubmit: boolean;
    isLoading: boolean;
    content: ReactNode;
    formId: string;
  };
}) {
  const showEdit = modalEdit?.open ?? false;
  const showCreate = !showEdit && (modalCreate?.open ?? false);
  const modalOpen = open || showCreate || showEdit;
  const widestShell = showCreate;
  const wideShell = showCreate || showEdit;

  return (
    <AppModal
      open={modalOpen}
      onOpenChange={(next) => {
        if (!next) {
          modalCreate?.setOpen(false);
          modalEdit?.setOpen(false);
        }
        setOpen(next);
      }}
      title={
        showEdit
          ? "Update contact details"
          : showCreate
            ? "Create contact"
            : "Select contact"
      }
      size="3xl"
      isDirty={
        (showCreate && (modalCreate?.isDirty ?? false)) ||
        (showEdit && (modalEdit?.isDirty ?? false))
      }
      loading={
        (showCreate && (modalCreate?.isLoading ?? false)) ||
        (showEdit && (modalEdit?.isLoading ?? false))
      }
      className={cn(
        "z-[109] w-[calc(100vw-2rem)] transition-[max-width,width,height] duration-200",
        widestShell
          ? "h-[min(92vh,980px)] max-w-7xl"
          : wideShell
            ? "h-[min(86vh,860px)] max-w-5xl"
            : "h-[min(80vh,780px)] max-w-2xl",
      )}
      bodyClassName="min-h-0 px-2 pt-3 pb-2"
      bodyContained
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      footer={
        showEdit && modalEdit ? (
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              setOpen={(nextOpen) => {
                if (!nextOpen) {
                  modalEdit.setOpen(false);
                }
                setOpen(nextOpen);
              }}
              isDirty={modalEdit.isDirty}
            />
            <Button
              type="submit"
              form={modalEdit.formId}
              disabled={!modalEdit.canSubmit || modalEdit.isLoading}
            >
              {modalEdit.isLoading ? (
                <LoadingSpinner className="h-4 w-4" />
              ) : (
                "Save"
              )}
            </Button>
          </div>
        ) : showCreate && modalCreate ? (
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              setOpen={(nextOpen) => {
                if (!nextOpen) {
                  modalCreate.setOpen(false);
                }
                setOpen(nextOpen);
              }}
              isDirty={modalCreate.isDirty}
            />
            <Button
              type="submit"
              form={EMBEDDED_CREATE_CONTACT_FORM_ID}
              disabled={!modalCreate.canSubmit || modalCreate.isLoading}
              data-testid="form-submit-button"
            >
              {modalCreate.isLoading ? (
                <LoadingSpinner className="h-4 w-4" />
              ) : (
                "Save"
              )}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          {showEdit && modalEdit ? (
            <div
              key="edit-contact"
              className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            >
              {modalEdit.content}
            </div>
          ) : showCreate && modalCreate ? (
            <div
              key="create-contact"
              className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            >
              {modalCreate.content}
            </div>
          ) : (
            <motion.div
              key="select-contact"
              className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden px-1 pb-1"
              initial={false}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppModal>
  );
}

function PendingSelectedContactCard({
  tone,
  className,
}: {
  tone: "default" | "client" | "clientGray" | "defendant";
  className?: string;
}) {
  if (tone === "default") {
    return (
      <div
        className={cn(
          "bg-card border-border flex h-full w-full items-center justify-center rounded-xl border shadow-sm",
          className,
        )}
      >
        <LoadingSpinner className="h-6 w-6" />
      </div>
    );
  }

  const gradientClass =
    tone === "clientGray"
      ? MATTER_PRIMARY_CONTACT_HERO_GRADIENT_PANEL_CLASS
      : tone === "defendant"
        ? MATTER_DEFENDANT_HERO_GRADIENT_PANEL_CLASS
        : MATTER_CLIENT_HERO_GRADIENT_PANEL_CLASS;

  return (
    <div className={cn("relative w-full min-w-0", className)}>
      <div
        className={cn(
          "flex min-h-[240px] w-full min-w-0 flex-col",
          gradientClass,
        )}
      >
        <div className="relative z-10 flex min-h-[240px] min-w-0 shrink-0 flex-col items-center justify-center">
          <LoadingSpinner className="h-6 w-6 text-white" />
        </div>
        <div
          className="relative z-10 mt-0 max-h-0 overflow-hidden opacity-0"
          aria-hidden
        />
      </div>
    </div>
  );
}

export interface AddContactFormFieldProps {
  label?: string;
  /** Optional action text for the empty contact slot. */
  selectContactLabel?: string;
  type?: HTMLInputElement["type"];
  className?: string;
  disableDelete?: boolean;
  disableSelectContactButton?: boolean;
  register: UseFormRegisterReturn;
  disabled?: boolean;
  placeholder?: string;
  excludeContactIds?: string[];
  setSelectedContact?: (contact: Contact) => void;
  defaultSearchObject?: { [key: string]: string[] };
  onDelete?: (contact: ContactPreviewChipContact) => void;
  allowMultiple?: boolean;
  defaultSearchField?: string;
  /** Renders the “assign client” row; use `disabled` when `client` is missing so layout stays stable. */
  showSelectClientButton?: boolean;
  showSelectDefendantButton?: boolean;
  defendants?: Contact[]; // New prop for multiple defendants
  client?: Contact;
  matterId?: string; // New prop to fetch matter data
  defaultValues?: Partial<z.infer<typeof createContactFormSchema>>;
  autoOpenContactSlideover?: boolean;
  parentSlideoverOpen?: boolean;
  /** Show search + table in the parent surface (no nested contact slideover). */
  inlineContactPicker?: boolean;
  /** When set, choosing a row calls this instead of only updating the form (e.g. submit immediately). */
  onContactSelectedForSubmit?: (contact: Contact) => void | Promise<void>;
  /**
   * With `inlineContactPicker`: runs synchronously after a row passes filters, **before** hydrate +
   * `onContactSelectedForSubmit`. Use to close a parent shell and snapshot form state so the list
   * does not sit open during network work.
   */
  onInlineContactPickInitiated?: () => void;
  createNewContactLabel?: string;
  /**
   * `compact` uses a shorter empty/selected slot (e.g. insurance modal related contacts).
   * `default` keeps the tall slideover-style selector.
   */
  contactSelectorSize?: "default" | "compact";
  /**
   * When `contactSelectorSize` is `compact`, skip the extra label row above the card and the
   * "Change" action (the card header already shows the role). Pencil, delete, and empty-state
   * selection still work.
   */
  hideCompactContactChrome?: boolean;
  /**
   * `modal` uses the centered `AppModal` contact picker (2xl). `slideover` keeps the large sheet.
   * Default is `modal` so pickers match the rest of the app.
   */
  onInlineEmbeddedCreateStateChange?: (state: {
    active: boolean;
    dirty: boolean;
    loading: boolean;
  }) => void;
  /** Controls embedded inline create-contact mode when parent wants to own the trigger/footer. */
  inlineEmbeddedCreateOpen?: boolean;
  onInlineEmbeddedCreateOpenChange?: (open: boolean) => void;
  /** Hides the default in-body create-contact action for inline pickers. */
  hideInlineCreateContactAction?: boolean;
  /**
   * `client` = green matter-client gradient; `clientGray` = slate primary-contact style;
   * `defendant` = blue defendant gradient (same shell as Matter → Defendant hero).
   * `matterRole` = per-contact gradient from their role on this matter (client / defendant /
   * other → primary gray), using `client`/`defendants` props when set, else `matter` connections.
   */
  contactPreviewTone?:
    | "default"
    | "client"
    | "clientGray"
    | "defendant"
    | "matterRole";
  /**
   * When set, replaces the default selected-contact card with custom UI (e.g. matter modal preview column).
   */
  renderSelectedContactPreview?: (contact: Contact) => ReactNode;
  /** Suppress the embedded create-contact success toast when parent shows a combined success message. */
  showEmbeddedCreateSuccessToast?: boolean;
}

export const AddContactFormField = ({
  label,
  selectContactLabel,
  disableDelete,
  disableSelectContactButton = false,
  className,
  register,
  setSelectedContact,
  excludeContactIds,
  defaultSearchObject,
  allowMultiple = false,
  onDelete,
  defaultSearchField,
  showSelectClientButton = false,
  showSelectDefendantButton = false,
  defendants,
  client,
  matterId,
  defaultValues,
  autoOpenContactSlideover = false,
  parentSlideoverOpen,
  inlineContactPicker = false,
  onContactSelectedForSubmit,
  onInlineContactPickInitiated,
  createNewContactLabel,
  contactSelectorSize = "default",
  hideCompactContactChrome = false,
  onInlineEmbeddedCreateStateChange,
  inlineEmbeddedCreateOpen,
  onInlineEmbeddedCreateOpenChange,
  hideInlineCreateContactAction = false,
  contactPreviewTone = "default",
  renderSelectedContactPreview,
  showEmbeddedCreateSuccessToast = true,
}: AddContactFormFieldProps) => {
  const form = useFormContext();
  const { hasActionPermission, hasPagePermission } = useUser();
  const canSearchContactDirectory = hasPagePermission(
    PAGE_PERMISSION_KEYS.contactsAll,
  );
  const [open, setOpen] = useState(false);
  const [uncontrolledIsAddingContact, setUncontrolledIsAddingContact] =
    useState(false);
  const [createShellDirty, setCreateShellDirty] = useState(false);
  const [createShellCanSubmit, setCreateShellCanSubmit] = useState(false);
  const [createShellLoading, setCreateShellLoading] = useState(false);
  const createShellLoadingRef = useRef(false);
  const [pendingSelectedContact, setPendingSelectedContact] =
    useState<Contact | null>(null);
  const prevFormContactIdRef = useRef<string | undefined>(undefined);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactBeingEdited, setContactBeingEdited] = useState<Contact | null>(
    null,
  );
  const [editShellDirty, setEditShellDirty] = useState(false);
  const [editShellLoading, setEditShellLoading] = useState(false);
  const [showOnlyMatterContacts, setShowOnlyMatterContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const debouncedContactSearch = useDebouncedValue(contactSearch, 250);
  /** Multi-select: IDs still running `contact selection` after row pick (modal closes first). */
  const [contactSelectionPendingIds, setContactSelectionPendingIds] = useState<
    string[]
  >([]);
  /** Same tick / parallel row handlers cannot append duplicate contactIds before form state catches up. */
  const multiSelectPickInflightRef = useRef<Set<string>>(new Set());
  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
    resetSearchObject,
  } = useSearchObject({ defaultSearchObject });
  const clearContactFilters = useCallback(() => {
    setContactSearch("");
    setShowOnlyMatterContacts(false);
    void clearSearchObject();
  }, [clearSearchObject]);
  const resolvedDefaultSearchField = defaultSearchField ?? "name";
  const contactSearchToolbar = (
    <div className="flex min-w-0 items-center gap-3 py-1">
      <ContactsGeneralSearch
        value={contactSearch}
        onChange={setContactSearch}
        className="min-w-0 flex-1 sm:w-auto"
      />
      <div className="shrink-0">
        <ContactsSearchBar
          addSearchObjectValue={addSearchObjectValue}
          removeSearchObjectValue={removeSearchObjectValue}
          searchObject={searchObject}
          defaultSearchField={resolvedDefaultSearchField}
          fullWidth
        />
      </div>
    </div>
  );

  // Track the contact ID being edited to refetch when slideover closes
  const editingContactIdRef = useRef<string | null>(null);

  // Fetch matter data if matterId is provided
  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(matterId || ""),
    enabled: !!matterId,
  });

  // Use passed matter or fetched matter data
  const matter = matterData?.data;

  const watchContactId = !allowMultiple
    ? (form.watch(register.name) as Contact | undefined)?.id
    : undefined;

  /**
   * Pending is only for the brief selection gap after picking a row. If it outlives a form
   * reset/save (new values from server), it must not override `form` or keep the spinner shell.
   */
  useEffect(() => {
    if (allowMultiple) {
      return;
    }
    if (watchContactId) {
      setPendingSelectedContact(null);
    } else if (prevFormContactIdRef.current) {
      setPendingSelectedContact(null);
    }
    prevFormContactIdRef.current = watchContactId;
  }, [allowMultiple, watchContactId]);

  // Use defendants array if provided, otherwise fall back to single defendant for backward compatibility
  const defendantsList = useMemo(() => defendants ?? [], [defendants]);
  const hasMultipleDefendants = defendantsList.length > 1;
  const hasSingleDefendant = defendantsList.length === 1;

  const [defendantDropdownOpen, setDefendantDropdownOpen] = useState(false);

  const getCompactContactRoleTone = useCallback(
    (contact: Contact): CompactContactRoleTone => {
      if (client?.id && contact.id === client.id) {
        return "client";
      }
      if (defendantsList.some((defendant) => defendant.id === contact.id)) {
        return "defendant";
      }
      const connections = matter ? getMatterContactConnections(matter) : [];
      if (connections.length > 0) {
        const labels = connections
          .filter((c) => c.contactId === contact.id)
          .map((c) => c.label);
        if (labels.includes("Client")) {
          return "client";
        }
        if (
          labels.some((l) => l === "Defendant" || l === "Statute Defendant")
        ) {
          return "defendant";
        }
      }
      return "default";
    },
    [client?.id, defendantsList, matter],
  );

  const openEditContact = (contact: Contact) => {
    const contactId = contact.id;
    if (!contactId) {
      return;
    }
    editingContactIdRef.current = contactId;
    setIsAddingContact(false);
    setContactBeingEdited(contact);
    setIsEditingContact(true);
    setOpen(false);
  };

  useEffect(() => {
    if (!isEditingContact) {
      setContactBeingEdited(null);
      setEditShellDirty(false);
      setEditShellLoading(false);
    }
  }, [isEditingContact]);

  // Show toggle only if we have matter data (either passed or fetched)
  const showMatterToggle = !!matter;
  const createContactButtonLabel = createNewContactLabel ?? "Create contact";
  const isAddingContact =
    inlineEmbeddedCreateOpen ?? uncontrolledIsAddingContact;
  const setIsAddingContact = useCallback(
    (next: boolean) => {
      onInlineEmbeddedCreateOpenChange?.(next);
      if (inlineEmbeddedCreateOpen === undefined) {
        setUncontrolledIsAddingContact(next);
      }
    },
    [inlineEmbeddedCreateOpen, onInlineEmbeddedCreateOpenChange],
  );

  useEffect(() => {
    createShellLoadingRef.current = createShellLoading;
  }, [createShellLoading]);

  const setInlineCreateOpen = useCallback(
    (next: boolean) => {
      if (!next && inlineContactPicker && createShellLoadingRef.current) {
        return;
      }

      setIsAddingContact(next);
    },
    [inlineContactPicker, setIsAddingContact],
  );

  const startCreatingContact = useCallback(() => {
    setIsEditingContact(false);
    setContactBeingEdited(null);
    editingContactIdRef.current = null;
    setIsAddingContact(true);
  }, [setIsAddingContact]);

  const startCreatingContactInModal = useCallback(() => {
    startCreatingContact();
    setOpen(true);
  }, [startCreatingContact]);

  useEffect(() => {
    if (!autoOpenContactSlideover || inlineContactPicker) return;
    if (parentSlideoverOpen) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [autoOpenContactSlideover, inlineContactPicker, parentSlideoverOpen]);

  useEffect(() => {
    if (inlineContactPicker) {
      return;
    }
    if (open) {
      void resetSearchObject();
    }
  }, [inlineContactPicker, open, resetSearchObject]);

  useEffect(() => {
    if (!isAddingContact) {
      setCreateShellDirty(false);
      setCreateShellCanSubmit(false);
      setCreateShellLoading(false);
    }
  }, [isAddingContact]);

  useEffect(() => {
    if (inlineContactPicker && parentSlideoverOpen === false) {
      setContactSearch("");
      setIsAddingContact(false);
    }
  }, [inlineContactPicker, parentSlideoverOpen, setIsAddingContact]);

  useEffect(() => {
    // Inline pickers render the table inside a parent modal and never drive `open`;
    // it stays false, so tying cleanup to `!open` would fire whenever `setIsAddingContact`
    // is recreated (e.g. after toggling embedded create) and immediately clear create mode.
    if (inlineContactPicker) {
      return;
    }
    if (!open) {
      setContactSearch("");
      setShowOnlyMatterContacts(false);
      setIsAddingContact(false);
      void clearSearchObject();
    }
  }, [clearSearchObject, inlineContactPicker, open, setIsAddingContact]);

  useEffect(() => {
    if (!inlineContactPicker || !onInlineEmbeddedCreateStateChange) {
      return;
    }
    onInlineEmbeddedCreateStateChange({
      active: isAddingContact,
      dirty: isAddingContact && createShellDirty,
      loading: isAddingContact && createShellLoading,
    });
  }, [
    inlineContactPicker,
    isAddingContact,
    createShellDirty,
    createShellLoading,
    onInlineEmbeddedCreateStateChange,
  ]);

  const isCompactSelector = contactSelectorSize === "compact";
  const emptySelectorShellClass = isCompactSelector
    ? CONTACT_ROLE_SELECT_EMPTY_SHELL_COMPACT_CLASS
    : CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS;
  const emptySelectorHeightClass = isCompactSelector
    ? CONTACT_ROLE_SELECT_CARD_COMPACT_HEIGHT_CLASS
    : CONTACT_ROLE_SELECT_CARD_HEIGHT_CLASS;
  /**
   * Filled compact gradient card: height follows content (no fixed min-height).
   * `self-start` avoids grid/flex row stretch adding dead space under the card.
   */
  /** Collapsed summary band height; details expand in a sibling below. */
  const compactContactSelectedInnerClass = `${emptySelectorHeightClass} shrink-0 overflow-hidden`;
  /** `space-y-0` overrides FormItem’s default `space-y-2` so the card isn’t pushed down. */
  const compactFormItemClassName = "flex w-full flex-col space-y-0 self-start";
  const selectedFormItemClass = isCompactSelector
    ? compactFormItemClassName
    : cn(
        "flex h-full w-full flex-col",
        contactPreviewTone !== "default" ? "p-0" : "bg-accent rounded-md p-1",
      );
  const selectedChipWrapperClass = "flex h-full w-full min-w-0";
  const selectedChipClassName = "h-full w-full min-w-0";
  return (
    <>
      <FormField
        control={form.control}
        {...register}
        // @ts-expect-error This line is to remove a console error
        ref={null}
        render={({ field }) => {
          let returnBuilder = <></>;
          const formContact = !allowMultiple
            ? (form.watch(field.name) as Contact | undefined)
            : undefined;
          const singleSelectedContact = allowMultiple
            ? null
            : getContactRecordId(formContact)
              ? formContact
              : (pendingSelectedContact ?? undefined);
          const contacts = allowMultiple
            ? readMultiSelectContactsField(form.watch(field.name))
            : singleSelectedContact
              ? [singleSelectedContact]
              : [];
          const isPendingSelectedCard =
            !allowMultiple &&
            getContactRecordId(pendingSelectedContact) !== "" &&
            getContactRecordId(formContact) === "";
          const hasSecondarySelectOption =
            showSelectClientButton ||
            (showSelectDefendantButton && defendantsList.length > 0);
          const showOnlySecondarySelectOption =
            disableSelectContactButton && hasSecondarySelectOption;
          const clientButtonLabel = showOnlySecondarySelectOption
            ? "Assign client"
            : "or assign client";
          const defendantButtonLabel = showOnlySecondarySelectOption
            ? "Assign defendant"
            : "or assign defendant";
          const assignSuggestedContact = (contact: Contact) => {
            if (allowMultiple) {
              const existing = readMultiSelectContactsField(
                form.getValues(field.name),
              );
              if (existing.some((item) => item.id === contact.id)) return;
              appendContactToMultiSelectField(form, field.name, contact);
            } else {
              form.setValue(field.name, contact, { shouldDirty: true });
            }
            setSelectedContact?.(contact);
          };
          if (
            inlineContactPicker &&
            !allowMultiple &&
            (!singleSelectedContact?.id || isAddingContact)
          ) {
            return (
              <FormItem
                className={cn(
                  className,
                  "w-full",
                  "flex min-h-0 flex-1 flex-col",
                )}
              >
                <div className="flex w-full shrink-0">
                  {label && <FormLabel>{label}</FormLabel>}
                  <FormMessage
                    data-testid="error-message"
                    className="grow text-right"
                  />
                </div>
                <FormControl className="flex min-h-0 flex-1 flex-col">
                  <div
                    className={cn("flex min-h-0 min-w-0 flex-1 flex-col gap-4")}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      {isAddingContact ? (
                        <motion.div
                          key="inline-create-contact"
                          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -16 }}
                          transition={{
                            duration: 0.22,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                        >
                          <AddContactSlideover
                            contentOnly
                            open={isAddingContact}
                            setOpen={setInlineCreateOpen}
                            showCreateSuccessToast={
                              showEmbeddedCreateSuccessToast &&
                              !onContactSelectedForSubmit
                            }
                            onShellDirtyChange={setCreateShellDirty}
                            onShellCanSubmitChange={setCreateShellCanSubmit}
                            onShellLoadingChange={setCreateShellLoading}
                            afterCreateFn={async (res) => {
                              onInlineContactPickInitiated?.();
                              await onContactSelectedForSubmit?.(res.data);
                              setIsAddingContact(false);
                            }}
                            defaultValues={defaultValues}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="inline-select-contact"
                          className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 16 }}
                          transition={{
                            duration: 0.22,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                        >
                          {showMatterToggle && (
                            <div className="flex items-center gap-3 rounded-md border p-3">
                              <Switch
                                checked={
                                  showOnlyMatterContacts ||
                                  !canSearchContactDirectory
                                }
                                disabled={!canSearchContactDirectory}
                                onCheckedChange={setShowOnlyMatterContacts}
                              />
                              <div className="flex flex-col gap-1">
                                <Typography className="font-medium">
                                  Show only matter-connected contacts
                                </Typography>
                                <Typography
                                  variant="subHeading"
                                  className="text-muted-foreground"
                                >
                                  Toggle to filter contacts connected to this
                                  case
                                </Typography>
                              </div>
                            </div>
                          )}
                          <ContactsTable
                            contextMatterId={matterId}
                            generalSearch={debouncedContactSearch}
                            layout="list"
                            scrollContainer="internal"
                            searchBar={contactSearchToolbar}
                            excludeContactIds={excludeContactIds}
                            onRowClick={async (row) => {
                              const selectedContact = row.original;
                              onInlineContactPickInitiated?.();
                              await onContactSelectedForSubmit?.(
                                selectedContact,
                              );
                            }}
                            tableName={tanstackTableNames.contactsSlideover}
                            searchObject={searchObject}
                            onClearFilters={clearContactFilters}
                            matterIds={
                              (showOnlyMatterContacts ||
                                !canSearchContactDirectory) &&
                              matterId
                                ? [matterId]
                                : undefined
                            }
                            tableButtons={
                              hasActionPermission(
                                ACTION_PERMISSION_KEYS.contact,
                                ACTION_PERMISSION_VALUES.create,
                              ) &&
                              !hideInlineCreateContactAction && (
                                <Button
                                  size="md"
                                  variant="outline"
                                  onClick={() => {
                                    startCreatingContact();
                                  }}
                                >
                                  <PlusCircle className="h-4 w-4" />
                                  <span className="ml-2">
                                    {createContactButtonLabel}
                                  </span>
                                </Button>
                              )
                            }
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </FormControl>
              </FormItem>
            );
          }
          if (getContactRecordId(contacts[0]) !== "") {
            returnBuilder =
              contacts.length > 0 ? (
                <>
                  {contacts.map((contact: Contact) => {
                    const contactRecordId = getContactRecordId(contact);
                    const compactRoleTone = getCompactContactRoleTone(contact);
                    const compactRoleLabel =
                      compactContactRoleLabel(compactRoleTone);
                    const isRowHydratingPick =
                      allowMultiple &&
                      Boolean(contactRecordId) &&
                      contactSelectionPendingIds.includes(contactRecordId);
                    return (
                      <FormItem
                        key={contactRecordId}
                        className={cn(
                          className,
                          renderSelectedContactPreview
                            ? "m-0 min-h-0 space-y-2 border-0 bg-transparent p-0 shadow-none"
                            : selectedFormItemClass,
                        )}
                      >
                        {renderSelectedContactPreview ? (
                          <>
                            <div className="sr-only">
                              {label ? <FormLabel>{label}</FormLabel> : null}
                            </div>
                            <FormMessage />
                            <FormControl className="w-full max-w-full">
                              {renderSelectedContactPreview(contact)}
                            </FormControl>
                          </>
                        ) : isCompactSelector ? (
                          <>
                            <div className="flex w-full shrink-0 items-start justify-between gap-2">
                              {label ? (
                                hideCompactContactChrome ? (
                                  <div className="sr-only">
                                    <FormLabel>{label}</FormLabel>
                                  </div>
                                ) : (
                                  <FormLabel>{label}</FormLabel>
                                )
                              ) : null}
                              <FormMessage
                                data-testid="error-message"
                                className="mb-0 shrink-0 text-sm"
                              />
                            </div>
                            <FormControl className="flex w-full flex-col">
                              <div className="relative min-w-0">
                                <CompactRelatedContactHeroCard
                                  label={label}
                                  contact={contact}
                                  matter={matter}
                                  matterId={matterId}
                                  compactRoleTone={compactRoleTone}
                                  compactRoleLabel={compactRoleLabel}
                                  collapsedHeightClass={
                                    compactContactSelectedInnerClass
                                  }
                                  onEditContact={() => {
                                    openEditContact(contact);
                                  }}
                                  editLoading={false}
                                  onDelete={(deletedContact) =>
                                    onDelete?.(
                                      deletedContact as ContactPreviewChipContact,
                                    )
                                  }
                                  disableDelete={disableDelete}
                                  onChangeContact={
                                    hideCompactContactChrome
                                      ? undefined
                                      : () => {
                                          setOpen(true);
                                        }
                                  }
                                />
                                {isRowHydratingPick ? (
                                  <div className="bg-card/60 absolute inset-0 z-[2] flex items-center justify-center rounded-[inherit] backdrop-blur-[1px]">
                                    <LoadingSpinner className="h-6 w-6" />
                                  </div>
                                ) : null}
                              </div>
                            </FormControl>
                          </>
                        ) : (
                          <>
                            <div className="flex w-full">
                              <FormMessage
                                data-testid="error-message"
                                className="grow text-right"
                              />
                            </div>
                            <FormControl className="flex-1">
                              <div className="flex h-full w-full flex-col gap-1">
                                <div
                                  className={cn(
                                    selectedChipWrapperClass,
                                    (isPendingSelectedCard ||
                                      isRowHydratingPick) &&
                                      "relative",
                                  )}
                                >
                                  {isPendingSelectedCard ||
                                  isRowHydratingPick ? (
                                    <PendingSelectedContactCard
                                      tone={
                                        contactPreviewTone === "matterRole"
                                          ? "clientGray"
                                          : contactPreviewTone === "default"
                                            ? "default"
                                            : contactPreviewTone
                                      }
                                      className={selectedChipClassName}
                                    />
                                  ) : contactPreviewTone !== "default" ? (
                                    <MatterClientHeroPreviewCard
                                      contact={contact}
                                      matter={matter}
                                      matterId={matterId}
                                      slotLabel={
                                        label?.replace(/\*$/, "").trim() ??
                                        "Contact"
                                      }
                                      hideTypeChips
                                      onEdit={() => {
                                        openEditContact(contact);
                                      }}
                                      editLoading={false}
                                      onDelete={(deletedContact) =>
                                        onDelete?.(
                                          deletedContact as ContactPreviewChipContact,
                                        )
                                      }
                                      disableDelete={disableDelete}
                                      className={selectedChipClassName}
                                      tone={
                                        contactPreviewTone === "matterRole"
                                          ? matterHeroToneFromCompactRole(
                                              getCompactContactRoleTone(
                                                contact,
                                              ),
                                            )
                                          : contactPreviewTone === "clientGray"
                                            ? "gray"
                                            : contactPreviewTone === "defendant"
                                              ? "blue"
                                              : "green"
                                      }
                                    />
                                  ) : (
                                    <Suspense
                                      fallback={
                                        <div>
                                          <LoadingSpinner />
                                        </div>
                                      }
                                    >
                                      <ContactPreviewChip
                                        disableDelete={disableDelete}
                                        contact={
                                          contact as ContactPreviewChipContact
                                        }
                                        onDelete={onDelete}
                                        onClick={() => {
                                          openEditContact(contact);
                                        }}
                                        matter={matter}
                                        className={selectedChipClassName}
                                      />
                                    </Suspense>
                                  )}
                                </div>
                              </div>
                            </FormControl>
                          </>
                        )}
                        {!inlineContactPicker ? (
                          <ContactPickerShell
                            open={open}
                            setOpen={setOpen}
                            modalCreate={{
                              open: isAddingContact,
                              setOpen: setIsAddingContact,
                              isDirty: createShellDirty,
                              canSubmit: createShellCanSubmit,
                              isLoading: createShellLoading,
                              content: (
                                <AddContactSlideover
                                  contentOnly
                                  externalModalFooter
                                  open={isAddingContact}
                                  setOpen={setIsAddingContact}
                                  onShellDirtyChange={setCreateShellDirty}
                                  onShellCanSubmitChange={
                                    setCreateShellCanSubmit
                                  }
                                  onShellLoadingChange={setCreateShellLoading}
                                  afterCreateFn={(res) => {
                                    if (allowMultiple) {
                                      const next =
                                        appendContactToMultiSelectField(
                                          form,
                                          field.name,
                                          res.data,
                                        );
                                      // @ts-expect-error This is a workaround to set the selected contact
                                      setSelectedContact?.(next);
                                    } else {
                                      form.setValue(field.name, res.data, {
                                        shouldDirty: true,
                                      });
                                      setSelectedContact?.(res.data);
                                    }
                                    setIsAddingContact(false);
                                    setOpen(false);
                                  }}
                                  defaultValues={defaultValues}
                                />
                              ),
                            }}
                            modalEdit={
                              isEditingContact &&
                              contactBeingEdited?.id === contact.id
                                ? {
                                    open: true,
                                    setOpen: (next) => {
                                      setIsEditingContact(next);
                                      if (!next) {
                                        setContactBeingEdited(null);
                                        editingContactIdRef.current = null;
                                        setOpen(false);
                                      }
                                    },
                                    isDirty: editShellDirty,
                                    canSubmit:
                                      editShellDirty &&
                                      hasActionPermission(
                                        ACTION_PERMISSION_KEYS.contact,
                                        ACTION_PERMISSION_VALUES.update,
                                      ),
                                    isLoading: editShellLoading,
                                    formId: EDIT_CONTACT_MODAL_FORM_ID,
                                    content: (
                                      <EditContactForm
                                        open={isEditingContact}
                                        setOpen={(next) => {
                                          setIsEditingContact(next);
                                          if (!next) {
                                            setContactBeingEdited(null);
                                            editingContactIdRef.current = null;
                                            setOpen(false);
                                          }
                                        }}
                                        activeContactId={contact.id}
                                        activeContact={contactBeingEdited}
                                        hasEditPermission={hasActionPermission(
                                          ACTION_PERMISSION_KEYS.contact,
                                          ACTION_PERMISSION_VALUES.update,
                                        )}
                                        handleSubmit={(data) => {
                                          editingContactIdRef.current = null;
                                          if (allowMultiple) {
                                            const updatedContacts =
                                              contacts.map((c: Contact) =>
                                                c.id === contact.id ? data : c,
                                              );
                                            form.setValue(
                                              field.name,
                                              updatedContacts,
                                              {
                                                shouldDirty: true,
                                              },
                                            );
                                          } else {
                                            form.setValue(field.name, data, {
                                              shouldDirty: true,
                                            });
                                            setSelectedContact?.(data);
                                          }
                                        }}
                                        hideFooter
                                        formId={EDIT_CONTACT_MODAL_FORM_ID}
                                        livePreviewLayout
                                        onDirtyChange={setEditShellDirty}
                                        onMutationPendingChange={
                                          setEditShellLoading
                                        }
                                      />
                                    ),
                                  }
                                : undefined
                            }
                          >
                            {showMatterToggle && (
                              <div className="mb-4 flex items-center gap-3 rounded-md border p-3">
                                <Switch
                                  checked={
                                    showOnlyMatterContacts ||
                                    !canSearchContactDirectory
                                  }
                                  disabled={!canSearchContactDirectory}
                                  onCheckedChange={setShowOnlyMatterContacts}
                                />
                                <div className="flex flex-col gap-1">
                                  <Typography className="font-medium">
                                    Show only matter-connected contacts
                                  </Typography>
                                  <Typography
                                    variant="subHeading"
                                    className="text-muted-foreground"
                                  >
                                    Toggle to filter contacts connected to this
                                    case
                                  </Typography>
                                </div>
                              </div>
                            )}
                            <div className="min-h-0 flex-1">
                              <ContactsTable
                                contextMatterId={matterId}
                                generalSearch={debouncedContactSearch}
                                layout="list"
                                scrollContainer="internal"
                                searchBar={contactSearchToolbar}
                                excludeContactIds={excludeContactIds}
                                onRowClick={(row) => {
                                  const selectedContact = row.original;
                                  if (allowMultiple) {
                                    if (!selectedContact.id) {
                                      return;
                                    }
                                    const pickId = selectedContact.id;
                                    const inflight =
                                      multiSelectPickInflightRef.current;
                                    if (inflight.has(pickId)) {
                                      setOpen(false);
                                      return;
                                    }
                                    const existing =
                                      readMultiSelectContactsField(
                                        form.getValues(field.name),
                                      );
                                    if (existing.some((c) => c.id === pickId)) {
                                      setOpen(false);
                                      return;
                                    }
                                    inflight.add(pickId);
                                    try {
                                      appendContactToMultiSelectField(
                                        form,
                                        field.name,
                                        selectedContact,
                                      );
                                      const pendingId = pickId;
                                      setContactSelectionPendingIds((prev) =>
                                        prev.includes(pendingId)
                                          ? prev
                                          : [...prev, pendingId],
                                      );
                                      setOpen(false);

                                      try {
                                        const current =
                                          readMultiSelectContactsField(
                                            form.getValues(field.name),
                                          );
                                        form.setValue(
                                          field.name,
                                          current.map((c) =>
                                            c.id === selectedContact.id
                                              ? selectedContact
                                              : c,
                                          ),
                                          { shouldDirty: true },
                                        );
                                        setSelectedContact?.(selectedContact);
                                      } finally {
                                        setContactSelectionPendingIds((prev) =>
                                          prev.filter((id) => id !== pendingId),
                                        );
                                      }
                                    } finally {
                                      inflight.delete(pickId);
                                    }
                                    return;
                                  }

                                  setPendingSelectedContact(selectedContact);
                                  setOpen(false);

                                  form.setValue(field.name, selectedContact, {
                                    shouldDirty: true,
                                  });
                                  setSelectedContact?.(selectedContact);
                                  setPendingSelectedContact(null);
                                }}
                                tableName={tanstackTableNames.contactsSlideover}
                                tableButtons={
                                  hasActionPermission(
                                    ACTION_PERMISSION_KEYS.contact,
                                    ACTION_PERMISSION_VALUES.create,
                                  ) && (
                                    <Button
                                      size="md"
                                      variant="outline"
                                      onClick={() => {
                                        startCreatingContactInModal();
                                      }}
                                    >
                                      <PlusCircle className="h-4 w-4" />
                                      <span className="ml-2">
                                        {createContactButtonLabel}
                                      </span>
                                    </Button>
                                  )
                                }
                                searchObject={searchObject}
                                onClearFilters={clearContactFilters}
                                matterIds={
                                  (showOnlyMatterContacts ||
                                    !canSearchContactDirectory) &&
                                  matterId
                                    ? [matterId]
                                    : undefined
                                }
                              />
                            </div>
                          </ContactPickerShell>
                        ) : null}
                      </FormItem>
                    );
                  })}
                </>
              ) : (
                <></>
              );
          }
          if (!singleSelectedContact?.id || allowMultiple) {
            returnBuilder = (
              <>
                {allowMultiple
                  ? contacts.length > 0 && returnBuilder
                  : returnBuilder}
                <FormItem
                  className={cn(
                    className,
                    isCompactSelector
                      ? compactFormItemClassName
                      : cn(
                          "w-full space-y-2",
                          contactPreviewTone !== "default" &&
                            "flex h-full min-h-0 flex-col",
                        ),
                  )}
                >
                  {!isCompactSelector ? (
                    <>
                      <div className="flex w-full shrink-0 items-start gap-2">
                        <FormMessage
                          data-testid="error-message"
                          className="grow text-right"
                        />
                      </div>
                      <FormControl
                        className={
                          contactPreviewTone !== "default"
                            ? "flex min-h-0 w-full flex-1 flex-col"
                            : undefined
                        }
                      >
                        <div className={emptySelectorShellClass}>
                          {!showOnlySecondarySelectOption &&
                          !disableSelectContactButton ? (
                            <button
                              type="button"
                              className={CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS}
                              onClick={() => {
                                setOpen(true);
                              }}
                              aria-label={
                                selectContactLabel ??
                                contactRoleSelectPrompt(label)
                              }
                            />
                          ) : null}
                          <div
                            className={cn(
                              hasSecondarySelectOption
                                ? CONTACT_ROLE_SELECT_EMPTY_BODY_WITH_ACTIONS_CLASS
                                : CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
                              "gap-2",
                            )}
                          >
                            {!showOnlySecondarySelectOption ? (
                              <div
                                className={
                                  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS
                                }
                              >
                                <span className="bg-muted text-muted-foreground flex h-7 w-7 items-center justify-center rounded-full">
                                  <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                                </span>
                                <Typography className="text-foreground line-clamp-1 text-sm leading-tight font-medium">
                                  <LabelText>
                                    {selectContactLabel ??
                                      contactRoleSelectPrompt(label)}
                                  </LabelText>
                                </Typography>
                              </div>
                            ) : null}
                            {showSelectClientButton && (
                              <Button
                                disabled={!client}
                                size="sm"
                                title={
                                  !client
                                    ? "Select a client before assigning"
                                    : undefined
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (client) {
                                    assignSuggestedContact(client);
                                  }
                                }}
                                type="button"
                                variant="ghost"
                                className={cn(
                                  CONTACT_ROLE_SELECT_EMPTY_ACTION_CLASS,
                                  "text-muted-foreground hover:text-foreground h-auto min-h-0 w-full justify-center gap-1.5 px-2 py-0.5 disabled:opacity-40",
                                )}
                              >
                                <Typography
                                  className="flex items-center gap-2 text-sm"
                                  variant="subHeading"
                                >
                                  <UserCheck className="h-4 w-4 shrink-0" />{" "}
                                  {clientButtonLabel}
                                </Typography>
                              </Button>
                            )}
                            {showSelectDefendantButton &&
                              defendantsList.length > 0 && (
                                <div
                                  className={cn(
                                    CONTACT_ROLE_SELECT_EMPTY_ACTION_CLASS,
                                    "w-full shrink-0",
                                  )}
                                >
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (hasSingleDefendant) {
                                        form.setValue(
                                          field.name,
                                          defendantsList[0],
                                          {
                                            shouldDirty: true,
                                          },
                                        );
                                        setSelectedContact?.(defendantsList[0]);
                                      } else {
                                        setDefendantDropdownOpen(true);
                                      }
                                    }}
                                    type="button"
                                    variant="ghost"
                                    className="text-muted-foreground hover:text-foreground h-auto min-h-0 w-full justify-center gap-1.5 px-2 py-0.5"
                                  >
                                    <Typography
                                      className="flex items-center gap-2 text-sm"
                                      variant="subHeading"
                                    >
                                      <UserCheck className="h-4 w-4 shrink-0" />{" "}
                                      {defendantButtonLabel}
                                    </Typography>
                                  </Button>
                                  {hasMultipleDefendants && (
                                    <Select
                                      open={defendantDropdownOpen}
                                      onOpenChange={setDefendantDropdownOpen}
                                      onValueChange={(defendantId) => {
                                        const selectedDefendant =
                                          defendantsList.find(
                                            (d) => d.id === defendantId,
                                          );
                                        if (selectedDefendant) {
                                          form.setValue(
                                            field.name,
                                            selectedDefendant,
                                            {
                                              shouldDirty: true,
                                            },
                                          );
                                          setSelectedContact?.(
                                            selectedDefendant,
                                          );
                                        }
                                        setDefendantDropdownOpen(false);
                                      }}
                                    >
                                      <SelectTrigger className="absolute inset-0 opacity-0" />
                                      <SelectContent
                                        position="popper"
                                        align="start"
                                        side="top"
                                        sideOffset={1}
                                      >
                                        {defendantsList.map((defendant) => (
                                          <SelectItem
                                            key={defendant.id}
                                            value={defendant.id}
                                          >
                                            <Typography
                                              variant="subHeading"
                                              size="sm"
                                            >
                                              {formatContactsName(defendant) ||
                                                (
                                                  defendant as unknown as {
                                                    contactName?: string;
                                                  }
                                                ).contactName?.trim() ||
                                                defendant.groupName ||
                                                defendant.organizationName ||
                                                "Unknown defendant"}
                                            </Typography>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      </FormControl>
                    </>
                  ) : (
                    <>
                      <FormMessage
                        data-testid="error-message"
                        className="mb-0 text-sm"
                      />
                      <FormControl className="flex w-full flex-col">
                        <div className={emptySelectorShellClass}>
                          {!disableSelectContactButton &&
                            !showOnlySecondarySelectOption && (
                              <button
                                type="button"
                                className={
                                  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS
                                }
                                onClick={() => {
                                  setOpen(true);
                                }}
                                aria-label={
                                  selectContactLabel ??
                                  contactRoleSelectPrompt(label)
                                }
                              />
                            )}
                          <div
                            className={cn(
                              hasSecondarySelectOption
                                ? CONTACT_ROLE_SELECT_EMPTY_BODY_WITH_ACTIONS_CLASS
                                : CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
                              "gap-2",
                            )}
                          >
                            {!showOnlySecondarySelectOption ? (
                              <div
                                className={
                                  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS
                                }
                              >
                                <span className="bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                                  <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                                </span>
                                <Typography className="text-foreground line-clamp-1 w-full max-w-full text-sm leading-tight font-medium">
                                  <LabelText>
                                    {selectContactLabel ??
                                      contactRoleSelectPrompt(label)}
                                  </LabelText>
                                </Typography>
                              </div>
                            ) : null}
                            {showSelectClientButton ||
                            (showSelectDefendantButton &&
                              defendantsList.length > 0) ? (
                              <div
                                className={cn(
                                  CONTACT_ROLE_SELECT_EMPTY_ACTION_CLASS,
                                  "flex w-full min-w-0 shrink-0 flex-col gap-0",
                                )}
                              >
                                {showSelectClientButton ? (
                                  <Button
                                    disabled={!client}
                                    size="sm"
                                    title={
                                      !client
                                        ? "Select a client before assigning"
                                        : undefined
                                    }
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (client) {
                                        assignSuggestedContact(client);
                                      }
                                    }}
                                    type="button"
                                    variant="ghost"
                                    className="text-muted-foreground hover:text-foreground h-auto min-h-0 w-full justify-center gap-1.5 px-2 py-0.5 disabled:opacity-40"
                                  >
                                    <Typography
                                      className="flex items-center gap-2 text-sm"
                                      variant="subHeading"
                                    >
                                      <UserCheck className="h-4 w-4 shrink-0" />{" "}
                                      {clientButtonLabel}
                                    </Typography>
                                  </Button>
                                ) : null}
                                {showSelectDefendantButton &&
                                defendantsList.length > 0 ? (
                                  <div
                                    className={cn(
                                      CONTACT_ROLE_SELECT_EMPTY_ACTION_CLASS,
                                      "w-full shrink-0",
                                    )}
                                  >
                                    <Button
                                      size="sm"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (hasSingleDefendant) {
                                          form.setValue(
                                            field.name,
                                            defendantsList[0],
                                            {
                                              shouldDirty: true,
                                            },
                                          );
                                          setSelectedContact?.(
                                            defendantsList[0],
                                          );
                                        } else {
                                          setDefendantDropdownOpen(true);
                                        }
                                      }}
                                      type="button"
                                      variant="ghost"
                                      className="text-muted-foreground hover:text-foreground h-auto min-h-0 w-full justify-center gap-1.5 px-2 py-0.5"
                                    >
                                      <Typography
                                        className="flex items-center gap-2 text-sm"
                                        variant="subHeading"
                                      >
                                        <UserCheck className="h-4 w-4 shrink-0" />{" "}
                                        {defendantButtonLabel}
                                      </Typography>
                                    </Button>
                                    {hasMultipleDefendants ? (
                                      <Select
                                        open={defendantDropdownOpen}
                                        onOpenChange={setDefendantDropdownOpen}
                                        onValueChange={(defendantId) => {
                                          const selectedDefendant =
                                            defendantsList.find(
                                              (d) => d.id === defendantId,
                                            );
                                          if (selectedDefendant) {
                                            form.setValue(
                                              field.name,
                                              selectedDefendant,
                                              {
                                                shouldDirty: true,
                                              },
                                            );
                                            setSelectedContact?.(
                                              selectedDefendant,
                                            );
                                          }
                                          setDefendantDropdownOpen(false);
                                        }}
                                      >
                                        <SelectTrigger className="absolute inset-0 opacity-0" />
                                        <SelectContent
                                          position="popper"
                                          align="start"
                                          side="top"
                                          sideOffset={1}
                                        >
                                          {defendantsList.map((defendant) => (
                                            <SelectItem
                                              key={defendant.id}
                                              value={defendant.id}
                                            >
                                              <Typography
                                                variant="subHeading"
                                                size="sm"
                                              >
                                                {formatContactsName(
                                                  defendant,
                                                ) ||
                                                  (
                                                    defendant as unknown as {
                                                      contactName?: string;
                                                    }
                                                  ).contactName?.trim() ||
                                                  defendant.groupName ||
                                                  defendant.organizationName ||
                                                  "Unknown defendant"}
                                              </Typography>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </FormControl>
                    </>
                  )}
                  {!inlineContactPicker ? (
                    <ContactPickerShell
                      open={open}
                      setOpen={setOpen}
                      modalCreate={{
                        open: isAddingContact,
                        setOpen: setIsAddingContact,
                        isDirty: createShellDirty,
                        canSubmit: createShellCanSubmit,
                        isLoading: createShellLoading,
                        content: (
                          <AddContactSlideover
                            contentOnly
                            externalModalFooter
                            open={isAddingContact}
                            setOpen={setIsAddingContact}
                            onShellDirtyChange={setCreateShellDirty}
                            onShellCanSubmitChange={setCreateShellCanSubmit}
                            onShellLoadingChange={setCreateShellLoading}
                            afterCreateFn={(res) => {
                              if (allowMultiple) {
                                appendContactToMultiSelectField(
                                  form,
                                  field.name,
                                  res.data,
                                );
                              } else {
                                form.setValue(field.name, res.data, {
                                  shouldDirty: true,
                                });
                              }
                              setSelectedContact?.(res.data);
                              setIsAddingContact(false);
                              setOpen(false);
                            }}
                            defaultValues={defaultValues}
                          />
                        ),
                      }}
                      modalEdit={
                        !allowMultiple &&
                        isEditingContact &&
                        contactBeingEdited?.id ===
                          (field.value as Contact | undefined)?.id
                          ? {
                              open: true,
                              setOpen: (next) => {
                                setIsEditingContact(next);
                                if (!next) {
                                  setContactBeingEdited(null);
                                  editingContactIdRef.current = null;
                                  setOpen(false);
                                }
                              },
                              isDirty: editShellDirty,
                              canSubmit:
                                editShellDirty &&
                                hasActionPermission(
                                  ACTION_PERMISSION_KEYS.contact,
                                  ACTION_PERMISSION_VALUES.update,
                                ),
                              isLoading: editShellLoading,
                              formId: EDIT_CONTACT_MODAL_FORM_ID,
                              content: (
                                <EditContactForm
                                  open={isEditingContact}
                                  setOpen={(next) => {
                                    setIsEditingContact(next);
                                    if (!next) {
                                      setContactBeingEdited(null);
                                      editingContactIdRef.current = null;
                                      setOpen(false);
                                    }
                                  }}
                                  activeContactId={(field.value as Contact).id}
                                  activeContact={
                                    contactBeingEdited ??
                                    (field.value as Contact)
                                  }
                                  hasEditPermission={hasActionPermission(
                                    ACTION_PERMISSION_KEYS.contact,
                                    ACTION_PERMISSION_VALUES.update,
                                  )}
                                  handleSubmit={(data) => {
                                    editingContactIdRef.current = null;
                                    form.setValue(field.name, data, {
                                      shouldDirty: true,
                                    });
                                    setSelectedContact?.(data);
                                  }}
                                  hideFooter
                                  formId={EDIT_CONTACT_MODAL_FORM_ID}
                                  livePreviewLayout
                                  onDirtyChange={setEditShellDirty}
                                  onMutationPendingChange={setEditShellLoading}
                                />
                              ),
                            }
                          : undefined
                      }
                    >
                      {showMatterToggle && (
                        <div className="mb-4 flex items-center gap-3 rounded-md border p-3">
                          <Switch
                            checked={
                              showOnlyMatterContacts ||
                              !canSearchContactDirectory
                            }
                            disabled={!canSearchContactDirectory}
                            onCheckedChange={setShowOnlyMatterContacts}
                          />
                          <div className="flex flex-col gap-1">
                            <Typography className="font-medium">
                              Show only matter-connected contacts
                            </Typography>
                            <Typography
                              variant="subHeading"
                              className="text-muted-foreground"
                            >
                              Toggle to filter contacts connected to this case
                            </Typography>
                          </div>
                        </div>
                      )}
                      <div className="min-h-0 flex-1">
                        <ContactsTable
                          contextMatterId={matterId}
                          generalSearch={debouncedContactSearch}
                          layout="list"
                          scrollContainer="internal"
                          searchBar={contactSearchToolbar}
                          excludeContactIds={excludeContactIds}
                          onRowClick={(row) => {
                            const selectedContact = row.original;
                            if (allowMultiple) {
                              if (!selectedContact.id) {
                                return;
                              }
                              const pickId = selectedContact.id;
                              const inflight =
                                multiSelectPickInflightRef.current;
                              if (inflight.has(pickId)) {
                                setOpen(false);
                                return;
                              }
                              const existing = readMultiSelectContactsField(
                                form.getValues(field.name),
                              );
                              if (existing.some((c) => c.id === pickId)) {
                                setOpen(false);
                                return;
                              }
                              inflight.add(pickId);
                              try {
                                appendContactToMultiSelectField(
                                  form,
                                  field.name,
                                  selectedContact,
                                );
                                const pendingId = pickId;
                                setContactSelectionPendingIds((prev) =>
                                  prev.includes(pendingId)
                                    ? prev
                                    : [...prev, pendingId],
                                );
                                setOpen(false);

                                try {
                                  const current = readMultiSelectContactsField(
                                    form.getValues(field.name),
                                  );
                                  form.setValue(
                                    field.name,
                                    current.map((c) =>
                                      c.id === selectedContact.id
                                        ? selectedContact
                                        : c,
                                    ),
                                    { shouldDirty: true },
                                  );
                                  setSelectedContact?.(selectedContact);
                                } finally {
                                  setContactSelectionPendingIds((prev) =>
                                    prev.filter((id) => id !== pendingId),
                                  );
                                }
                              } finally {
                                inflight.delete(pickId);
                              }
                              return;
                            }

                            setPendingSelectedContact(selectedContact);
                            setOpen(false);

                            form.setValue(field.name, selectedContact, {
                              shouldDirty: true,
                            });
                            setSelectedContact?.(selectedContact);
                            setPendingSelectedContact(null);
                          }}
                          tableName={tanstackTableNames.contactsSlideover}
                          tableButtons={
                            hasActionPermission(
                              ACTION_PERMISSION_KEYS.contact,
                              ACTION_PERMISSION_VALUES.create,
                            ) && (
                              <Button
                                size="md"
                                variant="outline"
                                onClick={() => {
                                  startCreatingContactInModal();
                                }}
                              >
                                <PlusCircle className="h-4 w-4" />
                                <span className="ml-2">
                                  {createContactButtonLabel}
                                </span>
                              </Button>
                            )
                          }
                          searchObject={searchObject}
                          onClearFilters={clearContactFilters}
                          matterIds={
                            (showOnlyMatterContacts ||
                              !canSearchContactDirectory) &&
                            matterId
                              ? [matterId]
                              : undefined
                          }
                        />
                      </div>
                    </ContactPickerShell>
                  ) : null}
                </FormItem>
              </>
            );
          }
          return returnBuilder;
        }}
      />
    </>
  );
};
